import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {Box, Stack, Typography} from '@mui/material';
import {array, bool, func, number, object} from 'prop-types';
import React from 'react';
import AreaIndicator from '../common/AreaIndicator.jsx';
import {featureTitle} from '@app/util/features.js';
import DataGrid from '@components/common/DataGrid.jsx';
import ZoomToButton from '@components/common/ZoomToButton';

const FeaturesGrid = ({
  features,
  estimates,
  rowSelectionModel,
  onRowSelectionModelChange,
  onZoomToClicked,
  disableSelection = false,
  hideSelection = false,
  minCost = 0,
  collection,
}) => {
  const titleProperty = collection?.title_property;

  const columns = [
    {
      field: 'pl_collection_title', // made up field to deconflict from dynamic props
      headerName: `Title${titleProperty && titleProperty !== 'title' ? ` (${titleProperty})` : ''}`,
      flex: 1,
      valueGetter: (value, row) =>
        featureTitle({
          feature: row,
          collection,
        }),
      renderCell: ({value}) => {
        if (value) {
          return <div>{value}</div>;
        } else {
          return (
            <Box
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              Untitled feature
            </Box>
          );
        }
      },
    },
    {
      field: 'pl_area',
      headerName: 'Area',
      flex: 1,
      filterable: false,
      valueGetter: (value, row) => row.properties['pl:area'],
      renderCell: ({value}) => <AreaIndicator squareMeters={value} />,
    },
    {
      field: 'quota_cost',
      headerName: 'Quota cost',
      flex: 1,
      filterable: false,
      valueGetter: (value, row) =>
        estimates[row.properties['pl:ref']]?.cost || 0,

      renderCell: ({row, value}) => {
        return (
          <Stack
            direction="row"
            sx={{gap: 1, alignItems: 'center', height: '100%'}}
          >
            {row.properties['pl:area'] < minCost && (
              <WarningAmberOutlinedIcon fontSize="small" color="action" />
            )}
            <Typography variant="body2">
              <AreaIndicator squareMeters={value} />
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'hashid',
      headerName: 'Reference',
      flex: 1,
      valueGetter: (value, row) => row.properties['pl:ref'],
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 1,
      sortable: false,
      filterable: false,
      hideable: false,
      getActions: ({row}) => [
        <ZoomToButton
          data-testid="zoom-button"
          key="zoomTo"
          onClick={() => onZoomToClicked(row)}
        />,
      ],
    },
  ];

  return (
    <DataGrid
      disableColumnMenu
      checkboxSelection={!hideSelection}
      getRowId={row => row.properties['pl:ref']}
      onRowSelectionModelChange={onRowSelectionModelChange}
      rowSelectionModel={rowSelectionModel}
      columns={columns}
      rows={features}
      isRowSelectable={() => hideSelection || !disableSelection}
      disableMultipleRowSelection={hideSelection || disableSelection}
      sx={{
        background: 'inherit',
      }}
      slotProps={{
        root: {
          'data-testid': 'features-grid',
        },
      }}
    />
  );
};

FeaturesGrid.propTypes = {
  features: array,
  estimates: object,
  rowSelectionModel: object,
  onRowSelectionModelChange: func,
  onZoomToClicked: func,
  disableSelection: bool,
  hideSelection: bool,
  minCost: number,
  collection: object,
};

export default FeaturesGrid;
